import { useState } from 'react';
import { describeError } from '../api/client';
import { listTunnels, stopAllTunnels, stopTunnel } from '../api/endpoints';
import { useResource, useToast } from '../state';
import { formatRelative, formatUntil, pluralize } from '../lib/format';
import { PageHeader } from '../components/AppShell';
import { CopyButton } from '../components/CopyButton';
import { EmptyState, ErrorNotice, InlineNotice, LoadingRows, SectionTitle } from '../components/Feedback';
import { ConfirmButton } from '../components/Form';
import { Mono } from '../components/Pills';

const TUNNEL_COMMAND = 'verifyloop tunnel start --port 3000';

export function TunnelsPage(): JSX.Element {
  const toast = useToast();
  const [confirmStopAll, setConfirmStopAll] = useState(false);
  const tunnels = useResource(() => listTunnels(), [], { live: ['tunnel.updated'] });

  const list = tunnels.data?.tunnels ?? [];
  const connected = list.filter((tunnel) => tunnel.connected).length;

  return (
    <div className="page">
      <PageHeader
        title="Tunnels"
        subtitle="Reach an app that only exists on a developer’s machine. The CLI holds the connection; the control plane routes browser traffic into it."
        actions={
          <CopyButton value={TUNNEL_COMMAND} label="Copy CLI command" copiedLabel="Command copied" />
        }
      />

      <div className="panel panel--explain">
        <h2>What a tunnel is for</h2>
        <p>
          Most of what an agent breaks only exists locally: the branch it just wrote is on someone&apos;s
          laptop, not on staging. A tunnel makes that machine the target. Start it next to the dev server,
          and every run in this workspace can drive <code>http://127.0.0.1:&lt;port&gt;</code> through the
          control plane as if it were deployed.
        </p>
        <pre className="command">{TUNNEL_COMMAND}</pre>
        <ul className="bullets">
          <li>
            A tunnel is short-lived and tied to one API key: it expires on its own, and revoking the key
            revokes the tunnel.
          </li>
          <li>
            Runs targeting a tunnel are labelled in the Runs table, so evidence collected from a laptop is
            never confused with evidence collected from staging.
          </li>
          <li>
            Stopping a tunnel that still has active runs cancels nothing; it only revokes future traffic.
          </li>
        </ul>
      </div>

      <SectionTitle
        actions={
          list.length > 0 ? (
            confirmStopAll ? (
              <span className="row__actions">
                <span className="muted">Revoke every tunnel in this workspace?</span>
                <button
                  type="button"
                  className="btn btn--danger btn--small"
                  onClick={() => {
                    setConfirmStopAll(false);
                    void stopAllTunnels()
                      .then((result) => {
                        toast.success(`Stopped ${pluralize(result.stopped.length, 'tunnel')}`);
                        tunnels.reload();
                      })
                      .catch((error: unknown) => toast.error(describeError(error)));
                  }}
                >
                  Yes, stop all
                </button>
                <button type="button" className="btn btn--ghost btn--small" onClick={() => setConfirmStopAll(false)}>
                  Cancel
                </button>
              </span>
            ) : (
              <button type="button" className="btn btn--small btn--ghost" onClick={() => setConfirmStopAll(true)}>
                Stop all tunnels
              </button>
            )
          ) : null
        }
      >
        Live tunnels {list.length > 0 ? <span className="muted">({connected} connected)</span> : null}
      </SectionTitle>

      {tunnels.error ? <ErrorNotice error={tunnels.error} onRetry={tunnels.reload} /> : null}
      {tunnels.loading ? <LoadingRows rows={2} label="Loading tunnels" /> : null}

      {!tunnels.loading && list.length === 0 ? (
        <EmptyState title="No tunnels are open">
          <p>
            Nothing is connected. Start one from the machine running your app with{' '}
            <code>{TUNNEL_COMMAND}</code>.
          </p>
        </EmptyState>
      ) : null}

      {list.length > 0 ? (
        <ul className="rows">
          {list.map((tunnel) => (
            <li key={tunnel.clientId} className="row">
              <div className="row__main">
                <div className="row__title">
                  <strong>{tunnel.label}</strong>
                  <span className={`pill ${tunnel.connected ? 'pill--pass' : 'pill--neutral'}`}>
                    {tunnel.connected ? 'connected' : 'not connected'}
                  </span>
                  {tunnel.activeRuns > 0 ? (
                    <span className="pill pill--running">{pluralize(tunnel.activeRuns, 'active run')}</span>
                  ) : null}
                </div>
                <p className="row__meta">
                  <Mono title={tunnel.clientId}>{tunnel.clientId}</Mono>
                  <span>serving {tunnel.localEndpoint || '-'}</span>
                  <span>opened {formatRelative(tunnel.createdAt)}</span>
                  <span title={tunnel.expiresAt}>expires {formatUntil(tunnel.expiresAt)}</span>
                  <span>{tunnel.transport}</span>
                </p>
              </div>
              <div className="row__actions">
                <ConfirmButton
                  label="Stop"
                  confirmLabel="Really stop?"
                  onConfirm={() => {
                    void stopTunnel(tunnel.clientId)
                      .then(() => {
                        toast.success(`Tunnel ${tunnel.label} stopped`);
                        tunnels.reload();
                      })
                      .catch((error: unknown) => toast.error(describeError(error)));
                  }}
                />
              </div>
            </li>
          ))}
        </ul>
      ) : null}

      <InlineNotice tone="info">
        The dashboard lists tunnels but cannot start one: the connection is opened from the machine running
        your app, so it needs the project directory and the CLI. That is also why it is a separate command
        from <code>verifyloop test run --local</code>.
      </InlineNotice>
    </div>
  );
}